import { useState, useEffect, useRef } from 'react'
import { supabase, QuizSession } from '../lib/supabase'

export interface QuizStatistics {
  totalViews: number
  completionRate: number
  avgTimeSeconds: number
  completedSessions: number
  activeSessions: number
  abandonedSessions: number
  externalClickSessions: number
  funnelData: Array<{
    step: number
    label: string
    views: number
    pageId?: string
  }>
}

interface UseQuizStatisticsParams {
  quizId: string
  period: 'today' | 'yesterday' | '7d' | '30d' | 'all' | 'custom'
  customFrom?: string
  customTo?: string
  quizData?: {
    pages?: Array<{
      id?: string | number
      uniqueId?: string
      title?: string
      [key: string]: any
    }>
    [key: string]: any
  }
}

// Helper para converter período do estado para o formato do hook
export function normalizePeriod(period: 'today' | 'yesterday' | '7d' | '30d' | 'all' | 'custom'): 'today' | 'yesterday' | '7d' | '30d' | 'all' | 'custom' {
  return period
}

export function useQuizStatistics({
  quizId,
  period,
  customFrom,
  customTo,
  quizData
}: UseQuizStatisticsParams) {
  const [statistics, setStatistics] = useState<QuizStatistics | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Usar ref para rastrear mudanças no quizData (especialmente ordem das páginas)
  const quizDataRef = useRef<string>('')

  useEffect(() => {
    if (!quizId) {
      setLoading(false)
      setStatistics(null)
      return
    }

    // Verificar se quizData mudou (especialmente ordem das páginas)
    const currentQuizDataString = JSON.stringify(quizData?.pages?.map((p, index) => ({
      index,
      id: p.id,
      uniqueId: p.uniqueId,
      title: p.title
    })) || [])

    const quizDataChanged = quizDataRef.current !== currentQuizDataString
    if (quizDataChanged) {
      quizDataRef.current = currentQuizDataString
      console.log('📊 [useQuizStatistics] QuizData mudou, recalculando estatísticas', {
        oldLength: quizDataRef.current ? JSON.parse(quizDataRef.current).length : 0,
        newLength: JSON.parse(currentQuizDataString).length,
        pages: JSON.parse(currentQuizDataString).map((p: any) => ({ index: p.index, title: p.title }))
      })
    }

    loadStatistics()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizId, period, customFrom, customTo,
    // Usar serialização das páginas como dependência para detectar mudanças na ordem
    JSON.stringify(quizData?.pages?.map((p, index) => ({
      index,
      id: p.id,
      uniqueId: p.uniqueId,
      title: p.title
    })) || [])
  ])

  const getDateFilter = () => {
    const now = new Date()
    let fromDate: Date
    let toDate: Date = now

    switch (period) {
      case 'today':
        // Começo do dia local (00:00:00)
        fromDate = new Date(now)
        fromDate.setHours(0, 0, 0, 0)
        // Final do dia local (23:59:59) - opcional, lte(now) já cobre até agora
        break
      case 'yesterday':
        // Começo do dia ontem
        const yesterday = new Date(now)
        yesterday.setDate(yesterday.getDate() - 1)
        yesterday.setHours(0, 0, 0, 0)
        fromDate = yesterday

        // Final do dia ontem
        const yesterdayEnd = new Date(yesterday)
        yesterdayEnd.setHours(23, 59, 59, 999)
        toDate = yesterdayEnd
        break
      case '7d':
        fromDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
        break
      case '30d':
        fromDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
        break
      case 'custom':
        if (customFrom && customTo) {
          // Parse dates from DD/MM/YYYY format
          const [dayFrom, monthFrom, yearFrom] = customFrom.split('/')
          const [dayTo, monthTo, yearTo] = customTo.split('/')

          // Criar datas locais
          fromDate = new Date(parseInt(yearFrom), parseInt(monthFrom) - 1, parseInt(dayFrom))
          fromDate.setHours(0, 0, 0, 0)

          toDate = new Date(parseInt(yearTo), parseInt(monthTo) - 1, parseInt(dayTo))
          toDate.setHours(23, 59, 59, 999)

          return {
            from: fromDate,
            to: toDate
          }
        }
        return null
      case 'all':
      default:
        return null
    }

    return {
      from: fromDate!,
      to: toDate
    }
  }

  const loadStatistics = async () => {
    try {
      console.log('🔍 [useQuizStatistics] loadStatistics INICIADO', {
        quizId,
        period,
        hasQuizData: !!quizData,
        quizDataPagesCount: quizData?.pages?.length || 0,
        quizDataPages: quizData?.pages?.map((p, i) => ({
          index: i,
          id: p.id,
          uniqueId: p.uniqueId,
          title: p.title
        })) || []
      });

      setLoading(true)
      setError(null)

      const dateFilter = getDateFilter()

      let query = supabase
        .from('quiz_sessions')
        .select('*')
        .eq('quiz_id', quizId)

      if (dateFilter) {
        query = query
          .gte('created_at', dateFilter.from.toISOString())
          .lte('created_at', dateFilter.to.toISOString())
      }

      const { data: sessions, error: fetchError } = await query.order('created_at', { ascending: false })

      if (fetchError) throw fetchError

      console.log('🔍 [useQuizStatistics] Sessões carregadas:', {
        totalSessions: sessions?.length || 0,
        sessionsWithPageHistory: sessions?.filter(s => s.session_data?.page_history?.length > 0).length || 0
      });

      if (!sessions || sessions.length === 0) {
        console.log('🔍 [useQuizStatistics] Nenhuma sessão encontrada');
        setStatistics({
          totalViews: 0,
          completionRate: 0,
          avgTimeSeconds: 0,
          completedSessions: 0,
          activeSessions: 0,
          abandonedSessions: 0,
          funnelData: []
        })
        setLoading(false)
        return
      }

      // Calcular métricas básicas
      // Usar apenas sessões com histórico registrado (garante consistência com o funil)
      const sessionsWithHistory = sessions.filter(s =>
        s.session_data?.page_history && s.session_data.page_history.length > 0
      )
      const totalViews = sessionsWithHistory.length
      const completedSessions = sessionsWithHistory.filter(s => s.status === 'completed').length
      const activeSessions = sessionsWithHistory.filter(s => s.status === 'active').length
      const abandonedSessions = sessionsWithHistory.filter(s => s.status === 'abandoned').length
      const externalClickSessions = sessionsWithHistory.filter(s => s.session_data?.has_external_click === true).length
      const completionRate = totalViews > 0 ? (completedSessions / totalViews) * 100 : 0

      // Calcular tempo médio (sessões com histórico e tempo válido)
      const sessionsWithTime = sessionsWithHistory.filter(s =>
        typeof s.total_time_seconds === 'number' &&
        s.total_time_seconds > 0
      )

      const avgTimeSeconds = sessionsWithTime.length > 0
        ? Math.round(sessionsWithTime.reduce((sum, s) => sum + s.total_time_seconds, 0) / sessionsWithTime.length)
        : 0

      // Calcular funil baseado no page_history
      const funnelData: Array<{ step: number; label: string; views: number; pageId?: string }> = []

      console.log('🔍 [useQuizStatistics] INICIANDO cálculo do funil', {
        hasQuizData: !!quizData,
        quizDataPagesCount: quizData?.pages?.length || 0
      });

      // Criar mapa de pageId (uniqueId ou id) para informações da página do quiz atual
      // O pageId no page_history é gerado por getPageTrackingId que usa uniqueId ou id
      const quizPagesMap = new Map<string, { index: number; title: string; pageId: string }>()
      if (quizData?.pages) {
        console.log('🔍 [useQuizStatistics] Criando quizPagesMap das páginas do quiz:', quizData.pages.map((p, i) => ({
          index: i,
          id: p.id,
          uniqueId: p.uniqueId,
          title: p.title,
          pageId: p.uniqueId || p.id?.toString() || `page-${i}`
        })));

        quizData.pages.forEach((page, index) => {
          // Gerar pageId da mesma forma que getPageTrackingId faz
          const pageId = page.uniqueId || page.id?.toString() || `page-${index}`
          const pageTitle = page.title || `Página ${index + 1}`
          quizPagesMap.set(pageId, { index, title: pageTitle, pageId })

          // Também mapear por id numérico se diferente de uniqueId (compatibilidade)
          if (page.id !== undefined && page.uniqueId && page.id.toString() !== page.uniqueId) {
            quizPagesMap.set(page.id.toString(), { index, title: pageTitle, pageId })
          }
        })

        console.log('🔍 [useQuizStatistics] quizPagesMap criado:', Array.from(quizPagesMap.entries()).map(([key, value]) => ({
          key,
          ...value
        })));
      } else {
        console.warn('⚠️ [useQuizStatistics] SEM quizData.pages! Usando fallback');
      }

      // Contar views por página usando session_data.page_history
      const pageViewsMap = new Map<string, { count: number; pageIndex: number; pageId: string }>()

      sessions.forEach(session => {
        const pageHistory = session.session_data?.page_history || []

        if (pageHistory.length > 0) {
          console.log('🔍 [useQuizStatistics] Processando page_history de sessão:', {
            sessionId: session.id,
            pageHistoryCount: pageHistory.length,
            pageHistory: pageHistory.map((p: any) => ({ pageId: p.pageId, pageIndex: p.pageIndex }))
          });
        }

        pageHistory.forEach((page: any) => {
          const pageId = page.pageId || `page-${page.pageIndex}`
          const existing = pageViewsMap.get(pageId)

          if (existing) {
            existing.count++
          } else {
            pageViewsMap.set(pageId, {
              count: 1,
              pageIndex: page.pageIndex || 0,
              pageId: pageId
            })
          }
        })
      })

      console.log('🔍 [useQuizStatistics] pageViewsMap criado:', Array.from(pageViewsMap.entries()).map(([key, value]) => ({
        key,
        ...value
      })));

      // Se temos dados do quiz, usar a ordem e nomes das páginas do quiz
      if (quizPagesMap.size > 0 && quizData?.pages) {
        console.log('🔍 [useQuizStatistics] Usando ordem do quizData.pages para criar funil');

        // Criar funil baseado na ordem das páginas do quiz atual
        quizData.pages.forEach((page, index) => {
          // Gerar pageId da mesma forma que getPageTrackingId
          const pageId = page.uniqueId || page.id?.toString() || `page-${index}`
          const pageData = pageViewsMap.get(pageId)

          // Se não encontrou por uniqueId, tentar por id numérico
          const views = pageData?.count || pageViewsMap.get(page.id?.toString() || '')?.count || 0

          console.log(`🔍 [useQuizStatistics] Processando página ${index}:`, {
            index,
            title: page.title,
            pageId,
            views,
            foundByUniqueId: !!pageData,
            foundById: !!pageViewsMap.get(page.id?.toString() || '')
          });

          // Sempre adicionar todas as páginas do quiz na ordem correta
          funnelData.push({
            step: index + 1,
            label: page.title || `Página ${index + 1}`,
            views,
            pageId: pageId
          })
        })

        console.log('🔍 [useQuizStatistics] Funil criado com ordem do quiz:', funnelData.map(f => ({
          step: f.step,
          label: f.label,
          views: f.views,
          pageId: f.pageId
        })));
      } else {
        // Fallback: ordenar por pageIndex e criar array do funil
        const sortedPages = Array.from(pageViewsMap.values())
          .sort((a, b) => a.pageIndex - b.pageIndex)

        sortedPages.forEach((page, index) => {
          funnelData.push({
            step: index + 1,
            label: `Página ${index + 1}`,
            views: page.count,
            pageId: page.pageId
          })
        })

        // Se não houver page_history, usar current_page_index como fallback
        if (funnelData.length === 0) {
          const maxPageIndex = Math.max(...sessions.map(s => s.current_page_index || 0))

          for (let i = 0; i <= maxPageIndex; i++) {
            const views = sessions.filter(s => (s.current_page_index || 0) >= i).length
            funnelData.push({
              step: i + 1,
              label: i === 0 ? 'Tela de abertura' : `Página ${i + 1}`,
              views
            })
          }
        }
      }

      console.log('🔍 [useQuizStatistics] Estatísticas finais calculadas:', {
        totalViews,
        completionRate,
        avgTimeSeconds,
        completedSessions,
        activeSessions,
        abandonedSessions,
        funnelDataCount: funnelData.length,
        funnelData: funnelData.map(f => ({ step: f.step, label: f.label, views: f.views, pageId: f.pageId }))
      });

      setStatistics({
        totalViews,
        completionRate,
        avgTimeSeconds,
        completedSessions,
        activeSessions,
        abandonedSessions,
        externalClickSessions,
        funnelData
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar estatísticas')
      console.error('Erro ao carregar estatísticas:', err)
    } finally {
      setLoading(false)
    }
  }

  return {
    statistics,
    loading,
    error,
    refetch: loadStatistics
  }
}

